import { useState, useEffect } from 'react';
import objectToArray from '../../utils/objectToArray';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { child, onValue, ref, set, get } from 'firebase/database';
import { months } from '../../components/shared/UserStatistic/UserStatistic';

import { auth, database } from '../config';

export const getData = async () => {
  return auth.currentUser;
};

export interface IUser {
  uid: string;
  displayName: string;
  email: string;
  emailVerified: boolean;
  phoneNumber: string;
  photoURL: string;
  metadata: {
    a: string;
    b: string;
    creationTime: string;
    lastSignInTime: string;
  };
  refreshToken: string;
}

export const createUser = async (login: string, password: string) => {
  const databaseRef = ref(database);

  try {
    await createUserWithEmailAndPassword(auth, login, password);
    const user = auth.currentUser;
    if (user?.uid) {
      const data = { uid: user.uid, email: user.email };
      await set(child(databaseRef, `/users/${user.uid}`), data);
      await set(child(databaseRef, `/users/${user.uid}/notification`), '');
      await set(child(databaseRef, `/paying/${user.uid}/pay-link`), '');
      await set(child(databaseRef, `/paying/${user.uid}/created`), Date.now());
      await set(child(databaseRef, `/paying/${user.uid}/email`), login);
      await set(child(databaseRef, `/invitations/${user.uid}`), data);
      await set(child(databaseRef, `/private/${user.uid}`), data);
    }
    return user;
  } catch (e: any) {
    throw new Error(e);
  }
};

export const unlockUser = async (uid: string) => {
  const databaseRef = ref(database);

  const month = months[new Date().getMonth()];
  const year = new Date().getFullYear();

  try {
    if (uid) {
      const user = await get(child(databaseRef, `/paying/${uid}/pay-link`));
      if (user.val()) {
        await set(child(databaseRef, `/paying/${uid}/receipts`), {
          [Date.now()]: `${month}-${year}`,
        });
        await set(child(databaseRef, `/paying/${uid}/pay-link`), '');
      } else {
        console.log('no user found');
        throw new Error();
      }
    }
    return 'success';
  } catch (e) {
    throw new Error();
  }
};

export const createNotification = async (html: string, userId: string) => {
  const databaseRef = ref(database);

  try {
    const res = await set(child(databaseRef, `/users/${userId}/notification`), html);
    return res;
  } catch (e) {
    throw new Error();
  }
};

export const useNotification = () => {
  const databaseRef = ref(database);
  const [notification, setNotification] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const user = auth.currentUser;

    onValue(child(databaseRef, `/users/${user?.uid}/notification`), (snap) => {
      setNotification(snap?.val());
      setLoading(false);
    });
  }, []);

  return { notification, loading };
};

export const usePayLink = () => {
  const databaseRef = ref(database);
  const [link, setLink] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const user = auth.currentUser;

    onValue(child(databaseRef, `/paying/${user?.uid}/pay-link`), (snap) => {
      setLink(snap?.val());
      setLoading(false);
    });
  }, []);

  return { link, loading };
};

export const useUsers = () => {
  const databaseRef = ref(database);
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    onValue(child(databaseRef, `/users`), (snap) => {
      setUsers(objectToArray(snap?.val()));
      setLoading(false);
    });
  }, []);

  return { users, loading };
};

export const useUser = () => {
  const [user, setUser] = useState<{ email?: string | null }>({});
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    auth.onAuthStateChanged((user) => {
      setUser({ email: user?.email });
      setLoading(false);
    });
  }, []);

  return { user, loading };
};
